import { useCallback, useRef, useEffect, RefObject } from 'react';
import { ZoomState } from '../types';
import { GRID_SIZE } from '../constants';

interface UseCanvasRenderingProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  width: number;
  height: number;
  bgColor: string;
  zoomState: ZoomState;
}

export const useCanvasRendering = ({
  canvasRef,
  width,
  height,
  bgColor,
  zoomState
}: UseCanvasRenderingProps) => {
  const { scale, position } = zoomState;
  const requestRef = useRef<number | null>(null);
  const noisePatternRef = useRef<HTMLCanvasElement | null>(null);

  // Generate noise pattern once
  const generateNoisePattern = useCallback(() => {
    if (noisePatternRef.current) return noisePatternRef.current;

    const noiseCanvas = document.createElement('canvas');
    noiseCanvas.width = 200;
    noiseCanvas.height = 200;
    const noiseCtx = noiseCanvas.getContext('2d');

    if (!noiseCtx) return null;

    const imageData = noiseCtx.createImageData(noiseCanvas.width, noiseCanvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const noise = Math.random() * 30; // Adjust intensity
      data[i] = noise;     // R
      data[i + 1] = noise; // G
      data[i + 2] = noise; // B
      data[i + 3] = 25;    // A (low opacity for subtle effect)
    }

    noiseCtx.putImageData(imageData, 0, 0);
    noisePatternRef.current = noiseCanvas;
    return noiseCanvas;
  }, []);

  // Define the render function
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context state
    ctx.save();

    // Apply transformations (scale and position)
    ctx.translate(position.x, position.y);
    ctx.scale(scale, scale);

    const worldLeft = (-position.x) / scale;
    const worldTop = (-position.y) / scale;
    const worldWidth = canvas.width / scale;
    const worldHeight = canvas.height / scale;
    const padding = GRID_SIZE * 4;
    const paddedLeft = worldLeft - padding;
    const paddedTop = worldTop - padding;
    const paddedRight = worldLeft + worldWidth + padding;
    const paddedBottom = worldTop + worldHeight + padding;

    // Draw background anchored to world coordinates
    // Parse and darken the background color
    const darkenColor = (color: string) => {
      // If it's an RGB/RGBA color, darken it
      if (color.startsWith('rgb')) {
        const match = color.match(/\d+/g);
        if (match) {
          const [r, g, b] = match.map(Number);
          // Darken by reducing RGB values by 15%
          const darkenFactor = 0.85;
          return `rgb(${Math.round(r * darkenFactor)}, ${Math.round(g * darkenFactor)}, ${Math.round(b * darkenFactor)})`;
        }
      }
      // If it's a hex color, try to darken it
      if (color.startsWith('#')) {
        const hex = color.slice(1);
        const num = parseInt(hex, 16);
        const r = Math.round((num >> 16) * 0.85);
        const g = Math.round(((num >> 8) & 0x00FF) * 0.85);
        const b = Math.round((num & 0x0000FF) * 0.85);
        return `rgb(${r}, ${g}, ${b})`;
      }
      // Fallback to darker gray
      return '#2a2a2a';
    };

    ctx.fillStyle = darkenColor(bgColor);
    ctx.fillRect(paddedLeft, paddedTop, paddedRight - paddedLeft, paddedBottom - paddedTop);

    // Apply noise texture
    const noisePattern = generateNoisePattern();
    if (noisePattern) {
      const pattern = ctx.createPattern(noisePattern, 'repeat');
      if (pattern) {
        ctx.fillStyle = pattern;
        ctx.fillRect(paddedLeft, paddedTop, paddedRight - paddedLeft, paddedBottom - paddedTop);
      }
    }

    // Draw grid (for visual reference) aligned to world space
    const firstVertical = Math.floor(paddedLeft / GRID_SIZE) * GRID_SIZE;
    const lastVertical = Math.ceil(paddedRight / GRID_SIZE) * GRID_SIZE;
    const firstHorizontal = Math.floor(paddedTop / GRID_SIZE) * GRID_SIZE;
    const lastHorizontal = Math.ceil(paddedBottom / GRID_SIZE) * GRID_SIZE;

    ctx.strokeStyle = 'rgba(128, 128, 128, 0.2)';
    ctx.lineWidth = 1 / scale;

    for (let x = firstVertical; x <= lastVertical; x += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(x, paddedTop);
      ctx.lineTo(x, paddedBottom);
      ctx.stroke();
    }

    for (let y = firstHorizontal; y <= lastHorizontal; y += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(paddedLeft, y);
      ctx.lineTo(paddedRight, y);
      ctx.stroke();
    }

    // Restore context state
    ctx.restore();
  }, [canvasRef, bgColor, scale, position, generateNoisePattern]);

  // Initialize canvas and set up dimensions
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas dimensions
    canvas.width = width;
    canvas.height = height;

    // Initial render
    renderCanvas();
  }, [canvasRef, width, height, renderCanvas]);

  // Set up animation frame for smooth rendering during interactions
  useEffect(() => {
    // Cancel any existing animation frame
    if (requestRef.current !== null) {
      cancelAnimationFrame(requestRef.current);
    }
    
    // Schedule a new render
    requestRef.current = requestAnimationFrame(renderCanvas);
    
    return () => {
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [scale, position, bgColor, renderCanvas]);

  return { renderCanvas };
}; 
